import { ChevronRight, Clock, Instagram, Phone, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CONTACT } from "@/data/contact";
import { MARKET } from "@/market";

const LANGUAGES = MARKET.languages;

export function Footer() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
  };

  const cols = [
    {
      title: t("footer.product"),
      links: [
        { key: "features", href: "#features" },
        { key: "pricing", href: "#pricing" },
        { key: "integrations", href: "#integrations" },
        { key: "modules", href: "#modules" },
        { key: "apps", href: "#apps" },
      ],
    },
    {
      title: t("footer.support"),
      links: [
        { key: "faq", href: "#faq" },
        { key: "contact", href: "#cta" },
        { key: "telegram", href: CONTACT.telegram.href },
      ],
    },
  ];

  return (
    <footer id="about" className="relative bg-dark-bg border-t border-dark-border pt-16 pb-10 overflow-hidden">
      {/* Фоновый градиент как в Header */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 blur-[100px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 blur-[100px] rounded-full" />
      </div>

      <div className="relative mx-auto max-w-[1200px] px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
        {/* Колонка 1: Логотип и соцсети */}
        <div>
          {/* Логотип как в Header */}
          <div className="flex items-center gap-1.5">
            <img src="/SAMLY_LOGO.png" alt="Samly" className="h-8 w-auto" />
            <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-primary px-1.5 py-0.5 rounded bg-primary/15">
              POS
            </span>
          </div>

          <p className="mt-3 text-white/40 text-[14px] leading-[1.6] max-w-[220px]">{t("footer.description")}</p>

          {/* Социальные сети */}
          <div className="mt-5 flex items-center gap-2">
            <a
              href={CONTACT.telegram.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram"
              className="group w-9 h-9 rounded-lg bg-dark-card border border-dark-border flex items-center justify-center text-white/40 hover:text-white hover:bg-[#0088cc] hover:border-[#0088cc] transition-all duration-200 hover:scale-105"
            >
              <Send className="w-4 h-4 transition-transform group-hover:-rotate-12" />
            </a>
            <a
              href={CONTACT.instagram.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="group w-9 h-9 rounded-lg bg-dark-card border border-dark-border flex items-center justify-center text-white/40 hover:text-white hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:border-transparent transition-all duration-200 hover:scale-105"
            >
              <Instagram className="w-4 h-4 transition-transform group-hover:scale-110" />
            </a>
          </div>
        </div>

        {/* Колонки навигации */}
        {cols.map((col) => (
          <div key={col.title}>
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-white/60">{col.title}</p>
            <ul className="mt-4 space-y-2.5">
              {col.links.map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    className="group inline-flex items-center gap-1 text-[13px] text-white/40 hover:text-white transition-colors duration-200"
                  >
                    <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    <span className="group-hover:translate-x-0.5 transition-transform">
                      {t(`footer.links.${link.key}`)}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Колонка 4: Контакты и языки */}
        <div>
          <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-white/60">{t("footer.contacts")}</p>

          <ul className="mt-4 space-y-3 text-[13px] text-white/40">
            <li>
              <a
                href={CONTACT.phone.href}
                className="group inline-flex items-center gap-2 text-[15px] font-semibold text-white transition-colors hover:text-primary"
              >
                <Phone className="w-4 h-4 shrink-0 text-primary" />
                {CONTACT.phone.display}
              </a>
            </li>
            <li className="flex items-start gap-2 group">
              <Send className="w-4 h-4 shrink-0 mt-0.5" />
              <a
                href={CONTACT.telegram.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors group-hover:text-white"
              >
                {CONTACT.telegram.handle}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{t("contact.hours")}</span>
            </li>
            <li className="flex items-start gap-2 group">
              <span className="text-[15px] shrink-0 leading-none">🌐</span>
              <a
                href={CONTACT.website.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors group-hover:text-white"
              >
                {CONTACT.website.display}
              </a>
            </li>
          </ul>

          {/* Переключатель языка как в Header */}
          <div className="mt-6">
            <p className="text-[10px] font-medium tracking-wide uppercase text-white/30 mb-2">{t("footer.lang_label")}</p>
            <div className="inline-flex items-center bg-dark-card border border-dark-border rounded-full p-0.5">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`
                    px-3 py-1 rounded-full text-[11px] font-semibold transition-all duration-200
                    ${
                      i18n.language === lang.code ? "bg-primary text-white shadow-sm" : "text-white/50 hover:text-white"
                    }
                  `}
                >
                  {lang.short}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="relative mx-auto mt-12 max-w-[1200px] border-t border-dark-border px-6 pt-6">
        <p className="text-[12.5px] text-white/30">
          © {new Date().getFullYear()} Samly. {t("footer.copyright")}
        </p>
      </div>
    </footer>
  );
}
